import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState({ type: '', text: '' });
  const [verifiedToken, setVerifiedToken] = useState('');

  const API_URL = 'http://localhost:5000/api';

  // Timer for resend
  React.useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const validateEmail = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      errors.email = 'Please enter a valid email';
    }
    
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateOTP = () => {
    const errors = {};
    
    if (!otp) {
      errors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(otp)) {
      errors.otp = 'OTP must be 6 digits';
    }
    
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOTP = async () => {
    if (!validateEmail()) return;

    setLoading(true);
    setServerMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_URL}/password/forgot`, { email });

      if (response.data.success) {
        setStep(2);
        setTimer(600); // 10 minutes
        setServerMessage({
          type: 'success',
          text: 'OTP sent to your email! Please check your inbox.'
        });
      }
    } catch (error) {
      setServerMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send OTP'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!validateOTP()) return;

    setLoading(true);
    setServerMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_URL}/password/verify-otp`, { email, otp });

      if (response.data.success) {
        setVerifiedToken(response.data.token);
        setStep(3);
        setServerMessage({
          type: 'success',
          text: 'OTP verified! Please set your new password.'
        });
      }
    } catch (error) {
      setServerMessage({
        type: 'error',
        text: error.response?.data?.message || 'Invalid OTP'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!validatePassword()) return;

    setLoading(true);
    setServerMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API_URL}/password/reset`, {
        email,
        otp,
        newPassword,
        confirmPassword
      });

      if (response.data.success) {
        setServerMessage({
          type: 'success',
          text: 'Password reset successful! Redirecting to login...'
        });

        setTimeout(() => {
          navigate('/account');
        }, 2000);
      }
    } catch (error) {
      setServerMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reset password'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <section className="forgot-password py-80">
      <div className="container container-lg">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="text-center mb-40">
              <h1 className="mb-16">Reset Your Password</h1>
              <p className="text-gray-500">
                {step === 1 && "Enter your email to receive OTP"}
                {step === 2 && "Enter the OTP sent to your email"}
                {step === 3 && "Create your new password"}
              </p>
            </div>

            {serverMessage.text && (
              <div className={`alert alert-${serverMessage.type === 'success' ? 'success' : 'danger'} mb-24`}>
                {serverMessage.text}
              </div>
            )}

            <div className="border rounded-16 p-40 bg-white">
              {/* Step 1: Email */}
              {step === 1 && (
                <div>
                  <div className="mb-24">
                    <label className="form-label fw-medium">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}
                  </div>

                  <button
                    className="btn btn-main w-100 py-16"
                    onClick={handleSendOTP}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </button>
                </div>
              )}

              {/* Step 2: OTP Verification */}
              {step === 2 && (
                <div>
                  <div className="mb-16">
                    <p className="text-gray-600">Email: <strong>{email}</strong></p>
                  </div>

                  <div className="mb-24">
                    <label className="form-label fw-medium">
                      Enter OTP <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.otp ? 'is-invalid' : ''}`}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      maxLength="6"
                    />
                    {errors.otp && (
                      <small className="text-danger">{errors.otp}</small>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-24">
                    <small className="text-gray-500">
                      {timer > 0 ? (
                        <>⏰ OTP expires in: <span className="text-danger fw-medium">{formatTime(timer)}</span></>
                      ) : (
                        <span className="text-danger">OTP expired!</span>
                      )}
                    </small>
                    {timer === 0 && (
                      <button
                        className="btn btn-link p-0 text-main-600"
                        onClick={handleSendOTP}
                        disabled={loading}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>

                  <button
                    className="btn btn-main w-100 py-16"
                    onClick={handleVerifyOTP}
                    disabled={loading || otp.length !== 6}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>

                  <button
                    className="btn btn-link w-100 mt-16"
                    onClick={() => setStep(1)}
                  >
                    ← Back to email
                  </button>
                </div>
              )}

              {/* Step 3: New Password */}
              {step === 3 && (
                <div>
                  <div className="mb-24">
                    <label className="form-label fw-medium">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    {errors.newPassword && (
                      <small className="text-danger">{errors.newPassword}</small>
                    )}
                    <small className="text-gray-400">Minimum 6 characters</small>
                  </div>

                  <div className="mb-24">
                    <label className="form-label fw-medium">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                    {errors.confirmPassword && (
                      <small className="text-danger">{errors.confirmPassword}</small>
                    )}
                  </div>

                  <button
                    className="btn btn-main w-100 py-16"
                    onClick={handleResetPassword}
                    disabled={loading}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>

                  <button
                    className="btn btn-link w-100 mt-16"
                    onClick={() => setStep(2)}
                  >
                    ← Back to OTP
                  </button>
                </div>
              )}

              <div className="text-center mt-24">
                <Link to="/account" className="text-main-600">
                  ← Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForgotPassword;